/**
 * Gb74PersonalReport - GB人財ベーシック個人レポート (gb-org-questionnaire-74)
 * docs/mockups/12_モック_GB人財_ダミーレポート.html 準拠。
 *
 * gb74形式の scores から派生値 (gb74Report.ts) を算出して表示する。
 * ダイヤモンド / 強みTop3 / 思考と行動のスタイル / 役割適性 / 気をつけたいこと / 補完の相手 / CTA の7セクション。
 *
 * タイプ判定・コントラスト(鮮明/穏やか)・タグライン/気をつけたい/補完は確定仕様(正本04)準拠。
 * 役割適性・Shadow・軸別スタイル補完は仮ロジック (gb74Report.ts 参照)。
 *
 * これは「画面表示」用の縦1カラム版。PDF出力は A4横2ページに組み替えた
 * Gb74PersonalReportPdf を使う (中身の部品は Gb74ReportBlocks で共用)。
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  computeGbType,
  buildGb74Report,
  getAxisScores,
  unwrapGb74Scores,
} from '../utils/gb74Report';
import { analysisApi, type JudgmentPattern } from '../api/analysisApi';
import {
  Gb74CautionCard,
  Gb74DiamondRadar,
  Gb74ReportHeader,
  Gb74RoleList,
  Gb74StyleItem,
  Gb74TypeHeading,
  ROLE_NOTE,
  CAUTION_NOTE,
} from './Gb74ReportBlocks';
import './Gb74PersonalReport.scss';

interface Gb74PersonalReportProps {
  scores: unknown;
  /** ヘッダー表示用: 氏名 */
  userName?: string;
  /** ヘッダー右上: 回答日 (例: 2026.07.15)。未指定時は meta を後方互換で使用 */
  answerDate?: string;
  /** ヘッダー右上: 会社名 */
  companyName?: string;
  /** ヘッダー右上: 部署名 */
  departmentName?: string;
  meta?: string; // 後方互換 (回答日等の複合文字列)。answerDate 未指定時のフォールバック表示
  /** CTAボタンのハンドラ (任意。現行デザインでは未使用) */
  onNavigateToOrg?: () => void;
  /**
   * DB (judgment_patterns) の該当パターンを外部から注入する (任意)。
   * 指定時は内部の API 取得 (findPatternByScores) を行わず、これを唯一源に使う。
   * デモ (未認証で API 不可) で seed の desc を直接渡す用途。null=該当なし扱い。
   */
  patternOverride?: JudgmentPattern | null;
}

export const Gb74PersonalReport: React.FC<Gb74PersonalReportProps> = ({
  scores,
  userName,
  answerDate,
  companyName,
  departmentName,
  meta,
  patternOverride,
}) => {
  // DB (judgment_patterns) の該当パターンを軸スコアで取得し、後半の文言節を結線する。
  // undefined=取得前, null=該当なし/取得失敗 → いずれもフォールバック(正本04/テンプレ)。
  // patternOverride 指定時は API 取得せずそれを使う (デモ: seed 直接注入)。
  const hasOverride = patternOverride !== undefined;
  const [fetched, setFetched] = useState<JudgmentPattern | null | undefined>(undefined);
  useEffect(() => {
    if (hasOverride) return; // 注入時は取得しない
    const s = unwrapGb74Scores(scores);
    if (!s) {
      setFetched(null);
      return;
    }
    const a = getAxisScores(s);
    let cancelled = false;
    setFetched(undefined);
    analysisApi
      .findPatternByScores({ A: a.A, B: a.B, C: a.C, D: a.D })
      .then((p) => {
        if (!cancelled) setFetched(p);
      })
      .catch(() => {
        if (!cancelled) setFetched(null);
      });
    return () => {
      cancelled = true;
    };
  }, [scores, hasOverride]);

  const pattern = hasOverride ? patternOverride : fetched;
  const report = useMemo(() => buildGb74Report(scores, pattern), [scores, pattern]);

  if (!report) {
    return null; // gb74形式でない場合は呼び出し側でフォールバック
  }

  const { axis, roles, shadows, styles, caution } = report;

  // GBタイプ コード (design_images/個人.png ヘッダーの「〇 タイプ」)
  const gbType = computeGbType(axis);
  // ヘッダーの回答日 (answerDate 優先、無ければ後方互換で meta)
  const dateText = answerDate || meta;

  return (
    <div className="gb74r">
      {/* 01 あなたのダイヤモンド (design_images/個人.png = your-report.html 準拠) */}
      <section className="gb74r-section">
        <div className="gb74r-section-num">01</div>
        <div className="gb74r-section-title">あなたのダイヤモンド</div>
        <div className="gb74r-d1">
          <Gb74ReportHeader
            dateText={dateText}
            companyName={companyName}
            departmentName={departmentName}
            userName={userName}
          />

          <div className="gb74r-d1-main">
            <Gb74TypeHeading gbType={gbType} />
            <Gb74DiamondRadar axis={axis} />
          </div>
        </div>
      </section>

      {/* 02 思考と行動のスタイル */}
      <section className="gb74r-section">
        <div className="gb74r-section-num">02</div>
        <div className="gb74r-section-title">思考と行動のスタイル</div>
        <div className="gb74r-d-panel">
          <div className="gb74r-d-card">
            {styles.map((st, i) => (
              <Gb74StyleItem key={st.title} style={st} hasDivider={i > 0} />
            ))}
          </div>
        </div>
      </section>

      {/* 03 役割適性 */}
      <section className="gb74r-section">
        <div className="gb74r-section-num">03</div>
        <div className="gb74r-section-title">役割適性</div>
        <div className="gb74r-d-panel">
          <div className="gb74r-d-card">
            <Gb74RoleList roles={roles} />
          </div>
        </div>
        <div className="gb74r-note">{ROLE_NOTE}</div>
      </section>

      {/* 04 気をつけたいこと */}
      {(caution || shadows.length > 0) && (
        <section className="gb74r-section">
          <div className="gb74r-section-num">04</div>
          <div className="gb74r-section-title">気をつけたいこと</div>
          <div className="gb74r-d-panel">
            {caution && <Gb74CautionCard text={caution} />}
            {shadows.map((sh) => (
              <Gb74CautionCard key={sh.title} title={sh.title} text={sh.text} />
            ))}
          </div>
          <div className="gb74r-note">{CAUTION_NOTE}</div>
        </section>
      )}

      <div className="gb74r-divider" />
      <div className="gb74r-footer">
        <div>GB人財 ベーシック レポート｜GBbrain</div>
        <div className="gb74r-footer-sub">
          本レポートは持ち味を可視化するものであり、能力の優劣を評価するものではありません。
        </div>
      </div>
    </div>
  );
};

export default Gb74PersonalReport;
